import { AppContext, assets, AuthService, CashLessService, CheckboxTable, CheckboxTableColum, DatePicker, downloadURI, form2, Input, POApi, PulseOpsFormat, SelectSearch, sharedStyle, SortFieldInfo } from '@pulseops/common'
import { SectionCol, SectionContent, SectionRow } from '@pulseops/submission/common'
import React from 'react'
import { StyleSheet, TouchableOpacity, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ScrollView } from 'react-native-gesture-handler'
import { APImportedFilesHistoryForm } from './APApplyingPremiumForm'
import { Controller } from 'react-hook-form'
import { pipe } from 'fp-ts/lib/function'
import { ZIO } from '@mxt/zio'
import { useLoading } from '@mxt/zio-react'
import { useIsFocused } from '@react-navigation/native'

export const APImportedFilesHistory = () => {
  const { t } = useTranslation()
  const [isLoading, bindLoader] = useLoading(false)
  const isFocused = useIsFocused()
  const [pageSize, setPageSize] = React.useState(10)
  const [pageNum, setPageNum] = React.useState(0)
  const [total, setTotal] = React.useState(0)
  const [sortItem, setSortItem] = React.useState<SortFieldInfo>({ field: '', order: 'asc' })
  const [importedFileList, setImportedFileList] = React.useState<CashLessService.APImportedFileInfo[]>([])
  const { showToast, showGlobalLoading } = React.useContext(AppContext.AppContextInstance)
  const { base, handleSubmit } = form2.useForm(APImportedFilesHistoryForm.APImportedFilesHistoryC, {
    defaultValues: {
      fromDate: new Date(),
      toDate: new Date(),
      batchName: ''
    }
  })
  const displayedColumns: CheckboxTableColum[] = [
    {
      title: t('Batch Name'),
      fieldName: 'fileName'
    },
    {
      title: t('Imported By'),
      fieldName: 'importBy'
    },
    {
      title: t('Import Date Time'),
      fieldName: 'importDate'
    },
    {
      title: t('Total Transactions'),
      fieldName: 'totalRows',
      disabled: true
    },
    {
      title: t('Successful Transactions'),
      fieldName: 'totalCaseSuccess',
      disabled: true
    },
    {
      title: t('Failed Transactions'),
      fieldName: 'totalCaseFail',
      disabled: true
    },
    {
      title: t('Download File'),
      fieldName: '',
      disabled: true,
      rowRender: (selectedRow) => {
        const jobID = selectedRow.selectItem["jobId"] as string
        const fileName = selectedRow.selectItem["originalFileName"] as string
        return (
          <TouchableOpacity style={[sharedStyle.button, importStyles.downloadButton]} onPress={() => onFileDownLoadEvent(jobID, fileName)}>
            <Text style={[sharedStyle.textButton]}>{t('Download')}</Text>
          </TouchableOpacity>
        )
      }
    }
  ]

  React.useEffect(() => { showGlobalLoading(isLoading) }, [isLoading])

  React.useEffect(() => {
    // if (isFocused) {
    //   onSearchEvent()
    // }
    return () => {
      setPageNum(0)
      setPageSize(10)
      setTotal(0)
    }
  }, [isFocused])

  const onSearchEvent = (pageNo: number, pageSi: number) => handleSubmit((searchData) => {
    const submitedData: CashLessService.APImportedFileListRequest = {
      fromDate: PulseOpsFormat.serviceInquiry_StartOfDate(searchData.fromDate as Date, true).toISOString(),
      toDate: PulseOpsFormat.serviceInquiry_endOfDate(searchData.toDate as Date, true).toISOString(),
      fileName: searchData.batchName,
      page: pageNo,
      size: pageSi
    }
    pipe(
      CashLessService.getAPImportedFileList(submitedData),
      ZIO.fold(
        (error) => {
          setTotal(0)
          setImportedFileList([])
        },
        (response) => {
          const importedList = response.items.map((importItem) => {
            const fNameList = importItem.fileName.split('.')
            const customedFileName = fNameList.length > 0 ? fNameList[0] : ''
            return {
              ...importItem,
              fileName: customedFileName,
              originalFileName: importItem.fileName
            }
          })
          setTotal(response.totalElements)
          setImportedFileList(importedList)
          return response
        }
      ),
      bindLoader,
      ZIO.unsafeRun({})
    )
  })()

  const onFileDownLoadEvent = (jobID: string, fileName: string) => {
    if (jobID) {
      const url = `premium-collection-internal/premium/process-data/${jobID}/detail`
      pipe(
        ZIO.zipPar(AuthService.token, AuthService.getLoginType, POApi.buildUrl(url)),
        ZIO.flatMap(([token, loginType, downloadUrl]) =>
          ZIO.fromPromise(() =>
            fetch(downloadUrl, {
              method: 'GET',
              headers: {
                'Cache-control': 'no-cache',
                Pragma: 'no-cache',
                Authorization: `Bearer ${token}`,
                'X-Authen-Vendor': loginType
              }
            })
          )
        ),
        ZIO.flatMap((res) =>
          ZIO.zipPar(
            ZIO.succeed(res),
            ZIO.fromPromise(() => res.blob())
          )
        ),
        ZIO.map(([res, blob]) => {
          downloadURI(window.URL.createObjectURL(blob), fileName)
          return blob
        }),
        ZIO.mapError((error) => {
          showToast(t('common:DowloadFail'), 'error')
          return error
        }),
        bindLoader,
        ZIO.unsafeRun({})
      )
    }
  }

  const onPageChange = (pageNo: number, pageSi: number) => {
    setPageNum(pageNo)
    onSearchEvent(pageNo, pageSi)
  }

  const onRowsPerPageChange = (pageNo: number, pageSi: number) => {
    setPageSize(pageSi)
    onSearchEvent(pageNo, pageSi)
  }

  return (
    <ScrollView style={sharedStyle.wrapper}>
      <SectionContent>
        <SectionRow>
          <SectionCol>
            <Controller
              control={base.control}
              name="fromDate"
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <DatePicker
                  required={true}
                  label={t('ServiceInquiry:FromDate')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  maxDate={new Date()}
                  maxDateMessage={t('message:MS990032')}
                  minDate={undefined}
                  minDateMessage={''}
                  errorMessage={error?.message}
                />
              )}
            />
          </SectionCol>
          <SectionCol>
            <Controller
              control={base.control}
              name="toDate"
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <DatePicker
                  required={true}
                  label={t('ServiceInquiry:ToDate')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  maxDate={new Date()}
                  maxDateMessage={t('message:MS990032')}
                  minDate={undefined}
                  minDateMessage={''}
                  errorMessage={error?.message}
                />
              )}
            />
          </SectionCol>
          <SectionCol>
            <Controller
              control={base.control}
              name="batchName"
              render={({ field: { value, onChange, onBlur } }) => (
                <Input
                  title={t('BatchName')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
          </SectionCol>
        </SectionRow>
        <View style={importStyles.searchContainer}>
          <TouchableOpacity style={[sharedStyle.button, sharedStyle.btnRed, importStyles.searchButton]} onPress={() => onSearchEvent(pageNum, pageSize)}>
            <Text style={sharedStyle.textButtonRed}>{t('Search')}</Text>
          </TouchableOpacity>
        </View>
      </SectionContent>
      <CheckboxTable
        displayedColumns={displayedColumns}
        dataSource={importedFileList}
        stickyHeader
        isPagination={true}
        totalRecord={total}
        pageSize={pageSize}
        pageNum={pageNum}
        setPageNum={setPageNum}
        setPageSize={setPageSize}
        setTotalRecord={setTotal}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        sortItem={sortItem}
        setSortItem={setSortItem}
      />
    </ScrollView>
  )
}
const importStyles = StyleSheet.create({
  searchContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  searchButton: {
    marginLeft: 0,
    marginTop: 20,
    marginBottom: 0
  },
  downloadButton: {
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
  }
})